import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, forkJoin, Observable } from 'rxjs';
import { Lookup } from 'src/app/models/lookup.model';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { TextFormField, SingleSelectionFormField, NumberFormField, DateFormField, LabelFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { JurisdictionService } from 'src/app/services/jurisdiction.service';
import { OelTypeService } from 'src/app/services/oel-type.service';
import { UnitSymbols } from 'src/app/enums/units.enum';
import { TypeService } from 'src/app/services/type.service';
import { NoiseFrequencyRating } from 'src/app/enums/noiseFrequencyRating.enum';
import { NoiseSpeedType } from 'src/app/enums/noiseSpeedType.enum';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { MasterAgentService } from 'src/app/services/master-agent.service';
import { getOelCorrectionMethodLookup } from 'src/app/enums/oelCorrectionMethod.enum';
import { TWACorrectionMethod } from 'src/app/enums/TWACorrectionMethod.enum';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { AdjustmentClassificationEnum, AdjustmentClassificationEffect, getAdjustmentClassificationOptionLabel } from 'src/app/enums/adjustmentClassification.enum';
import { ILookup } from 'src/app/shared/form/lookup.model';

export interface IAgentJurisdictionData extends IFormDialogData {
  masterAgentId?: string;
  jurisdictionId?: string;
  isNewVersion?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-agent-jurisdiction-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss']
})
export class AgentJurisdictionDialogComponent extends FormComponent<AgentJurisdiction> implements OnInit {
  private static readonly SOURCE_REF_MAX_LENGTH = 500;

  agentsList: Lookup[] = [];
  jurisdictionsList: Lookup[] = [];
  oelTypeList: Lookup[] = [];

  constructor(
    public dialogRef: MatDialogRef<AgentJurisdictionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAgentJurisdictionData,
    protected injector: Injector,
    protected agentJurisdictionService: AgentJurisdictionService,
    private masterAgentService: MasterAgentService,
    private jurisdictionService: JurisdictionService,
    private oelTypeService: OelTypeService,
    private typeService: TypeService,
    private valueConversionService: ValueConversionService,
  ) {
    super(
      dialogRef,
      agentJurisdictionService,
      data,
      "Agent Jurisdiction",
      injector
    );
  }

  ngOnInit(): void {
    this.isFormLoading = true;

    forkJoin({
      entity: this.getEntity(),
      agents: this.masterAgentService.getAll(),
      jurisdictions: this.jurisdictionService.getAll(),
      oelTypes: this.oelTypeService.getAll(),
    }).pipe(finalize(() => { this.isFormLoading = false; }))
      .subscribe({
        next: (result) => {
          this.entity = result.entity;
          this.agentsList = result.agents;
          this.jurisdictionsList = result.jurisdictions;
          this.oelTypeList = result.oelTypes;
          this.initModel();
          this.initFields();
        },
        error: (error) => {
          this.toastService.showError(error);
        }
      });
  }

  get isNewVersion(): boolean {
    return !!this.data?.isNewVersion;
  }

  initModel() {
    if (this.isNewVersion) {
      this.entity.effectiveStart = null;
      this.entity.effectiveEnd = null;
      this.entity.sourceRef = null;
    }
    if (!this.isAdd) {
      if (this.entity.oel != null) {
        this.entity.oel = this.valueConversionService.getParameterValueAsPerAgentUnit(this.entity.oel, this.entity.agentUnits);
      }
      this.getAgentDetails();
    } else if (this.data?.masterAgentId) {
      this.entity.masterAgentId = this.data?.masterAgentId;
      this.getAgentDetails();
    }
    else if (this.data?.jurisdictionId) {
      this.entity.jurisdictionId = this.data?.jurisdictionId;
    }
  }

  initFields() {
    this.fields = [
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.masterAgentId),
        name: 'Agent',
        options: this.agentsList,
        columnSpan: new ColumnSpan(6, 6),
        onChange: () => this.getAgentDetails()
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.jurisdictionId),
        name: 'Jurisdiction',
        options: this.jurisdictionsList,
        columnSpan: new ColumnSpan(6, 6),
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.oelTypeId),
        name: 'OEL Type',
        columnSpan: new ColumnSpan(2, 6),
        options: this.oelTypeList,
      }),
      new NumberFormField({
        property: nameOf<AgentJurisdiction>(x => x.oel),
        name: 'OEL',
        required:false,
        columnSpan: new ColumnSpan(2, 6),
        tooltip: this.translateService.getDynamicTranslation('Leave blank if no OEL established'),
        min: 0.01
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.agentUnits),
        name: 'Unit Preference',
        options: this.typeService.getEnumOptions(UnitSymbols),
        columnSpan: new ColumnSpan(2, 6)
      }),
      new TextFormField({
        property: nameOf<AgentJurisdiction>(x => x.exchangeRate),
        name: 'Exchange Rate',
        columnSpan: new ColumnSpan(2, 6),
        required: true
      }),
      new TextFormField({
        property: nameOf<AgentJurisdiction>(x => x.threshold),
        name: 'Threshold',
        columnSpan: new ColumnSpan(2, 6),
        required: true
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.noiseFrequencyRating),
        name: 'Noise Frequency Rating',
        options: this.typeService.getEnumOptions(NoiseFrequencyRating),
        columnSpan: new ColumnSpan(2, 6), required: false
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.noiseSpeed),
        name: 'Noise Speed',
        options: this.typeService.getEnumOptions(NoiseSpeedType),
        columnSpan: new ColumnSpan(2, 6), required: false
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.oelCorrectionMethod),
        name: 'OEL Correction Method',
        options: getOelCorrectionMethodLookup(),
        columnSpan: new ColumnSpan(2, 6), required: false
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.adjustmentClassification),
        name: 'Adjustment Classification',
        options: this.buildAdjustmentClassificationOptions(),
        tooltip: this.translateService.getDynamicTranslation('Leave blank to inherit the substance\'s global classification.'),
        columnSpan: new ColumnSpan(2, 6),
        required: false,
        onChange: () => this.updateAdjustmentBasis(),
      }),
      new LabelFormField({
        property: 'adjustmentClassificationBasis',
        name: '',
        required: false,
        columnSpan: new ColumnSpan(6, 6),
        cssClass: 'info-banner',
      }),
      new SingleSelectionFormField({
        property: nameOf<AgentJurisdiction>(x => x.twaCorrectionMethod),
        name: 'TWA Correction Method',
        options: getEnumLookup(TWACorrectionMethod),
        columnSpan: new ColumnSpan(2, 6), required: false
      }),
      ...this.buildEffectiveDatingFields(),
    ];

    this.updateAdjustmentBasis();

    if (this.isNewVersion) {
      const lockedProperties: string[] = [
        nameOf<AgentJurisdiction>(x => x.masterAgentId),
        nameOf<AgentJurisdiction>(x => x.jurisdictionId),
        nameOf<AgentJurisdiction>(x => x.oelTypeId),
      ];
      this.fields.forEach(field => {
        if (lockedProperties.includes(field.property)) {
          field.readonly = true;
        }
      });
      this.updateBanner();
    }
  }

  private buildEffectiveDatingFields(): (LabelFormField | DateFormField | TextFormField)[] {
    return [
      new LabelFormField({
        property: 'effectiveDatingHeading',
        name: 'Effective Dating',
        required: false,
        columnSpan: new ColumnSpan(6, 6),
        cssClass: 'section-subhead',
      }),
      new LabelFormField({
        property: 'newVersionBanner',
        name: '',
        required: false,
        columnSpan: new ColumnSpan(6, 6),
        cssClass: 'info-banner',
        isVisible: () => this.isNewVersion,
      }),
      new DateFormField({
        property: nameOf<AgentJurisdiction>(x => x.effectiveStart),
        name: 'Effective Start',
        required: this.isNewVersion,
        columnSpan: new ColumnSpan(3, 6),
        onChange: () => this.updateBanner(),
      }),
      new DateFormField({
        property: nameOf<AgentJurisdiction>(x => x.effectiveEnd),
        name: 'Effective End',
        required: false,
        columnSpan: new ColumnSpan(3, 6),
        isVisible: () => !this.isNewVersion,
      }),
      new TextFormField({
        property: nameOf<AgentJurisdiction>(x => x.sourceRef),
        name: 'Source',
        required: false,
        columnSpan: new ColumnSpan(6, 6),
      }),
    ];
  }

  private buildAdjustmentClassificationOptions(): ILookup[] {
    const classes = Object.keys(AdjustmentClassificationEffect).map(Number) as AdjustmentClassificationEnum[];
    return [
      { id: null, name: 'Inherit from agent' },
      ...classes.map(value => ({
        id: value,
        name: getAdjustmentClassificationOptionLabel(value, key => this.translateService.getDynamicTranslation(key)),
      })),
    ];
  }

  private updateAdjustmentBasis(): void {
    const basisField = this.fields?.find(field => field.property === 'adjustmentClassificationBasis');
    if (!basisField) {
      return;
    }
    // The Label field template applies `| translate` to field.name, so assign the raw i18n key.
    const value = this.entity?.adjustmentClassification;
    basisField.name = value != null
      ? AdjustmentClassificationEffect[value]
      : 'Inherits the agent\'s global adjustment classification.';
  }

  private updateBanner(): void {
    const bannerField = this.fields.find(field => field.property === 'newVersionBanner');
    if (!bannerField) {
      return;
    }
    const start = this.entity?.effectiveStart;
    if (!this.isNewVersion || !start) {
      bannerField.name = this.translateService.getDynamicTranslation('Creating a new version. Select an Effective Start date; the current version will be closed the day before and preserved in Version History.');
      return;
    }
    const startDisplay = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.toIsoDate(start), this.dateFormatService.userDateFormat);
    const prevDisplay = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.previousDay(start), this.dateFormatService.userDateFormat);
    bannerField.name = this.translateService.getDynamicTranslation(
      'Creating a new version effective {{startDate}}. The current version will be closed on {{prevDate}} and preserved in Version History.',
      { startDate: startDisplay, prevDate: prevDisplay },
    );
  }

  private toIsoDate(value: string | Date): string {
    // Never use `new Date(dateOnlyString)` here: it parses as UTC midnight and
    // reading it back with local getters shifts the date by one day in UTC-negative
    // timezones (e.g. es-CL). parseDateComponents() builds the Date from the exact
    // local calendar components, so the effective date is preserved on every save.
    const date = value instanceof Date ? value : this.dateFormatService.parseDateComponents(value);
    if (isNaN(date.getTime())) {
      return typeof value === 'string' ? value : '';
    }
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private previousDay(value: string | Date): string {
    const iso = this.toIsoDate(value);
    // addDays() parses/formats the canonical yyyy-MM-dd string with no timezone
    // interpretation, avoiding the new Date() + setDate() UTC-shift trap.
    return iso ? this.dateFormatService.addDays(iso, -1) : '';
  }

  getAgentDetails() {
    this.masterAgentService.getById(this.entity.masterAgentId).subscribe({
      next: (res) => {
        this.applyFieldDispositionForAgentType(res.agentType);
        this.updateFieldVisibility();
      },
      error: (error) => {
        this.toastService.showError('Failed to load agent details');
      }
    });
  }

  /**
   * Applies the Agent–Jurisdiction–OEL field disposition for the selected agent's type.
   *  - Noise:      show Exchange Rate, Threshold, Noise Frequency Rating, Noise Speed (Exchange Rate
   *                & Threshold mandatory). Noise is the ONLY type that uses this field set.
   *  - Biological: BEI mapping — show only OEL Type, OEL Value (optional) and Unit; hide and
   *                un-require every Air/Noise-specific field so the form can be submitted.
   *  - Everything else (Air, Wipe, Vibration, Other, any future type): the noise fields are
   *                hidden and un-required by default. This is an allowlist, not a denylist, so an
   *                unlisted type can never fall through to a mandatory noise field and render an
   *                unsubmittable form (previously Other did exactly that).
   * Runs on every agent change, so switching between agent types toggles the correct field set
   * without a page reload.
   */
  private applyFieldDispositionForAgentType(agentType: SampleTypeEnum): void {
    const isBiological = agentType === SampleTypeEnum.Biological;

    // Fields whose visibility already toggled between Air/Wipe (hidden) and Noise (shown).
    const noiseFields: string[] = [
      nameOf<AgentJurisdiction>(x => x.exchangeRate),
      nameOf<AgentJurisdiction>(x => x.threshold),
      nameOf<AgentJurisdiction>(x => x.noiseFrequencyRating),
      nameOf<AgentJurisdiction>(x => x.noiseSpeed)
    ];
    const mandatoryNoiseFields: string[] = [
      nameOf<AgentJurisdiction>(x => x.exchangeRate),
      nameOf<AgentJurisdiction>(x => x.threshold)
    ];
    // Fields shown for the legacy types (Air/Noise/Wipe) but irrelevant to a Biological agent.
    const nonBiologicalFields: string[] = [
      nameOf<AgentJurisdiction>(x => x.oelCorrectionMethod),
      nameOf<AgentJurisdiction>(x => x.adjustmentClassification),
      'adjustmentClassificationBasis',
      nameOf<AgentJurisdiction>(x => x.twaCorrectionMethod)
    ];

    // Allowlist: Noise is the only type that uses the exchange-rate/threshold/frequency/speed set.
    // Every other type hides and un-requires them, so no unlisted type can render a mandatory noise
    // field it has no way to satisfy.
    const hideNoiseFields = agentType !== SampleTypeEnum.Noise;

    this.fields.forEach(field => {
      if (noiseFields.includes(field.property)) {
        field.isVisible = () => !hideNoiseFields;
        if (hideNoiseFields) {
          this.entity[field.property] = null;
          field.required = false;
        } else if (mandatoryNoiseFields.includes(field.property)) {
          field.required = true;
        }
      } else if (nonBiologicalFields.includes(field.property)) {
        field.isVisible = () => !isBiological;
        if (isBiological) {
          this.entity[field.property] = null;
          field.required = false;
        }
      }
    });
  }

  validate(): boolean {
    if (this.isNewVersion && !this.entity.effectiveStart) {
      this.toastService.showError(this.translateService.getDynamicTranslation('Effective Start is required for a new version.'));
      return false;
    }
    if (this.entity.effectiveStart && this.entity.effectiveEnd) {
      const start = this.dateFormatService.parseDateComponents(this.toIsoDate(this.entity.effectiveStart));
      const end = this.dateFormatService.parseDateComponents(this.toIsoDate(this.entity.effectiveEnd));
      if (start >= end) {
        this.toastService.showError(this.translateService.getDynamicTranslation('Effective End must be after Effective Start.'));
        return false;
      }
    }
    if (this.entity.sourceRef && this.entity.sourceRef.length > AgentJurisdictionDialogComponent.SOURCE_REF_MAX_LENGTH) {
      this.toastService.showError(this.translateService.getDynamicTranslation('Source must be {{max}} characters or fewer.', { max: AgentJurisdictionDialogComponent.SOURCE_REF_MAX_LENGTH }));
      return false;
    }
    return true;
  }

  getNewObject(): AgentJurisdiction {
    return new AgentJurisdiction();
  }

  onBeforeSave(model: AgentJurisdiction) {
    if (model.oel != null) {
      model.oel = this.valueConversionService.convertAgentUnitValueToBase(model.oel, model.agentUnits);
    }
    model.effectiveStart = model.effectiveStart ? this.toIsoDate(model.effectiveStart) : null;
    model.effectiveEnd = model.effectiveEnd ? this.toIsoDate(model.effectiveEnd) : null;
    if (this.isNewVersion) {
      delete model.id;
      model.effectiveEnd = null;
    }
    // Strip client-side computed display fields so they are never posted to the backend.
    delete model.statusClass;
    delete model.statusLabel;
    delete model.isHistorical;
    delete model.effectivePeriodDisplay;
    delete model.oelValue;
  }

  override saveAction(model): Observable<Object> {
    return this.isNewVersion
      ? this.agentJurisdictionService.createVersion(model)
      : super.saveAction(model);
  }
}
